import { useMemo } from 'react';
import { DiningDrawerSeedContext, GetBookingMyMscResponse } from '@msc-b2c/interfaces/interfaces';
import { CartItem } from '@msc-b2c/interfaces/types';

type UseCartDiningSeedContextParams = Readonly<{
  selectedCartItem: CartItem | null;
  bookingData?: GetBookingMyMscResponse;
}>;

export const useCartDiningSeedContext = ({
  selectedCartItem,
  bookingData,
}: UseCartDiningSeedContextParams): DiningDrawerSeedContext | undefined => {
  const selectedPassengerIds = useMemo(
    () => new Set(selectedCartItem?.passengers?.map((passenger) => passenger.id) ?? []),
    [selectedCartItem?.passengers]
  );

  const diningSeedPassengers = useMemo(() => {
    if (!selectedCartItem || !bookingData?.cabins?.length || selectedPassengerIds.size === 0) {
      return undefined;
    }

    const passengersByCabin = bookingData.cabins.reduce((acc, cabin) => {
      if (!cabin.id) {
        return acc;
      }

      const cabinPassengerIds = (cabin.passengers ?? [])
        .map((passenger) =>
          typeof passenger === 'object' && passenger !== null
            ? (passenger as { id?: string }).id
            : (passenger as string)
        )
        .filter((id): id is string => Boolean(id));

      if (cabinPassengerIds.length > 0) {
        const selectedMap = cabinPassengerIds.reduce((paxAcc, passengerId) => {
          paxAcc[passengerId] = selectedPassengerIds.has(passengerId);
          return paxAcc;
        }, {} as Record<string, boolean>);

        if (Object.values(selectedMap).some(Boolean)) {
          acc[cabin.id] = selectedMap;
        }
      }

      return acc;
    }, {} as Record<string, Record<string, boolean>>);

    return Object.keys(passengersByCabin).length > 0 ? passengersByCabin : undefined;
  }, [bookingData?.cabins, selectedCartItem, selectedPassengerIds]);

  return useMemo(() => {
    if (!selectedCartItem?.diningSelection && !diningSeedPassengers) {
      return undefined;
    }

    const preselectedReservations = selectedCartItem?.diningSelection?.reservations?.filter(
      (reservation) => Boolean(reservation.day && reservation.hour && reservation.restaurantCode)
    );
    const firstReservation = preselectedReservations?.[0];

    return {
      ...(diningSeedPassengers && { preselectedPassengers: diningSeedPassengers }),
      ...(preselectedReservations?.length && { preselectedReservations }),
      ...(firstReservation?.day && { preselectedDate: firstReservation.day }),
      ...(firstReservation?.hour && { preselectedTime: firstReservation.hour }),
      ...(firstReservation?.restaurantCode && {
        preselectedDiningRoomCode: firstReservation.restaurantCode,
      }),
      ...(selectedCartItem?.diningSelection?.reservationId && {
        preselectedReservationId: selectedCartItem.diningSelection.reservationId,
      }),
      ...(typeof selectedCartItem?.diningSelection?.maximumSlotsBookable === 'number' &&
        selectedCartItem.diningSelection.maximumSlotsBookable > 0 && {
          maximumSlotsBookable: selectedCartItem.diningSelection.maximumSlotsBookable,
        }),
    };
  }, [diningSeedPassengers, selectedCartItem]);
};
